import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shared vocabulary, defined once.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * "Cell", "wave", "touch" and "holdout" are load-bearing terms with precise
 * meanings, and getting one wrong is expensive: mailing the holdout destroys
 * the only baseline the campaign has. They appeared as bare column headings
 * with nothing to explain them, and the difference between a cell and a wave
 * is genuinely not guessable from the words.
 *
 * Definitions live HERE rather than inline at each use, so the Doors filter,
 * the Drop planner and the Campaign readout cannot drift into describing the
 * same thing three different ways.
 *
 * No TooltipProvider is added: admin/layout.tsx wraps the app in
 * SidebarProvider, which already mounts one.
 */

export const GLOSSARY = {
  cell: (
    <>
      <b>A cell is one mailing.</b> The 5,360 doors are split into 18 balanced
      cells of about 285, assigned by hashing the ZIP and house number — so the
      split is random with respect to geography and size, and stable across
      rebuilds. Cells are what actually get mailed: a drop is one cell, one
      touch.
    </>
  ),
  wave: (
    <>
      <b>A wave is geography, not a mailing.</b> W1 Bourbon Country, W4 Midwest
      and so on. Waves are for filtering the list and reading results by region.
      Nothing is mailed by wave — <code>plan_drop()</code> selects on cell and
      ignores waves entirely.
    </>
  ),
  holdout: (
    <>
      <b>273 doors that are never mailed.</b> Their response rate is what would
      have happened anyway, so lift is the mailed rate minus that baseline — the
      only honest answer to "did the postcards do anything?". Mail them once and
      there is nothing left to measure against.
    </>
  ),
  potential: (
    <>
      <b>A human judgment about whether this door is worth pursuing.</b> Rated
      in the old HTML review page and carried over: <code>high</code>,{" "}
      <code>low</code>, or unrated. Only 26 of 5,363 doors have ever been rated,
      so <b>unrated is the normal state and is not a poor score</b> — it means
      nobody has looked yet.
    </>
  ),
  permits: (
    <>
      <b>The TTB permits held at this address.</b> Every door here holds a
      Distilled Spirits Plant permit — that is what puts it on the list — but
      908 of them also hold an Importer or Wholesaler permit. A distillery that
      also imports and wholesales is running a materially bigger operation than
      one that only distils.
    </>
  ),
  tenancy: (
    <>
      <b>Whether this address holds one business or several.</b>{" "}
      <code>sole_occupant</code> for 5,252 doors, <code>shared_address</code>{" "}
      for 111. It matters because a shared address is where two permits can look
      like one door, or one door like two — which is exactly what the merge
      questions in the review queue are about.
    </>
  ),
  name_source: (
    <>
      <b>Where the addressee came from.</b> <code>dba</code> is the trading name
      on the permit (3,214), <code>permit_owner</code> the legal owner where no
      trading name exists (2,122), and <code>override</code> means a person
      chose it (27) — the only one of the three that represents a human
      judgment, and the reason it survives a pipeline rebuild.
    </>
  ),
  touch: (
    <>
      <b>Which postcard in the sequence.</b> T01 is the first card a door
      receives, T02 the second. One drop is one cell at one touch.
    </>
  ),
  suppressed: (
    <>
      <b>Someone asked not to be mailed.</b> Permanent, honored for post and
      email alike, and a pipeline rebuild will not undo it. Distinct from a bad
      address, which is a Review-queue question.
    </>
  ),
  retired: (
    <>
      <b>No longer in the TTB file.</b> The permit stopped appearing, so the
      door is kept for history but excluded from mailing. Retiring is soft —
      nothing is ever deleted.
    </>
  ),
} as const;

/** An inline "?" that explains a term on hover or focus. */
export const Explain = ({
  term,
  children,
}: {
  term: keyof typeof GLOSSARY;
  children?: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        // A real button, so it is reachable by keyboard. A bare icon with a
        // title attribute is invisible to anyone not using a mouse.
        aria-label={`What is a ${term}?`}
        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground align-middle"
      >
        {children}
        <HelpCircle className="size-3.5 shrink-0" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs leading-relaxed">
      {GLOSSARY[term]}
    </TooltipContent>
  </Tooltip>
);
